import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { LogOut, Menu } from "lucide-react";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useParams } from "react-router-dom";
import { useChatHeader } from "../../contexts/ChatHeaderContext";
import NavItems from "../../features/Chat/components/SideBar/NavItems";
import UserPopover from "../../features/Chat/components/SideBar/UserPopover";
import ShopSwitcher from "../ShopSwitcher";
import { logoutRequest } from "../../features/Auth/slices/authActions";

export const TopNavBar = ({ onToggleSidebar }) => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const { shopId } = useParams();
  const shops = useSelector((state) => state.shops);
  const auth = useSelector((state) => state.auth);
  const currentShop = shops.data.find((s) => s._id === shopId);
  const { headerData } = useChatHeader();

  const roleLabel = getRoleLabel(auth.user?.role);
  const userName = auth.user?.name || auth.user?.username || "";
  const logoutHandler =
    headerData?.onLogoutClick ?? (() => dispatch(logoutRequest()));

  return (
    <header className="flex items-center justify-between h-10 px-3 bg-white border-b border-gray-100 text-gray-800 shrink-0">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isMobile && (
          <button
            onClick={onToggleSidebar}
            className="p-1.5 rounded hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-primary/30 touch-target-large"
            aria-label="Toggle menu"
          >
            {currentShop && currentShop.avatar ? (
              <div className="w-6 h-6 rounded-full overflow-hidden bg-gradient-to-br from-pink-500 to-purple-500">
                <img
                  src={currentShop.avatar}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        )}

        {headerData && !isMobile && (
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <ShopSwitcher
              shops={headerData.shops}
              shopGroups={headerData.shopGroups}
              activeGroup={headerData.activeGroup}
              currentUser={headerData.currentUser}
              selectedShopIds={headerData.selectedShopIds}
              changeShop={headerData.changeShop}
              onApplyMultiSelect={headerData.onApplyMultiSelect}
              onChangeGroup={headerData.onChangeGroup}
              singleSelect={headerData.singleSelect}
            />
            {!headerData.hideNavItems && (
            <NavItems
              role={headerData.role}
              onRoleChange={headerData.onRoleChange}
              activePresetId={headerData.activePresetId}
              onApplyPreset={headerData.onApplyPreset}
              shopId={headerData.shopId}
              staffId={headerData.staffId}
              accountId={headerData.accountId}
              pipelineStages={headerData.pipelineStages}
              currentFilterSnapshot={headerData.currentFilterSnapshot}
              onApplyView={headerData.onApplyView}
              tags={headerData.tags}
              tagZalos={headerData.tagZalos}
              managers={headerData.managers}
              selectedTags={headerData.selectedTags}
              selectedTagZalos={headerData.selectedTagZalos}
              selectedManagers={headerData.selectedManagers}
              selectedCategories={headerData.selectedCategories}
              selectedPipelineStageIds={headerData.selectedPipelineStageIds}
              needsReplyMinutes={headerData.needsReplyMinutes}
              newCustomerDays={headerData.newCustomerDays}
              hasContactInfo={headerData.hasContactInfo}
              upcomingBirthdayDays={headerData.upcomingBirthdayDays}
              onTagsChange={headerData.onTagsChange}
              onTagZalosChange={headerData.onTagZalosChange}
              onManagersChange={headerData.onManagersChange}
              onCategoriesChange={headerData.onCategoriesChange}
              onPipelineStagesChange={headerData.onPipelineStagesChange}
              onNeedsReplyMinutesChange={headerData.onNeedsReplyMinutesChange}
              onNewCustomerDaysChange={headerData.onNewCustomerDaysChange}
              onHasContactInfoChange={headerData.onHasContactInfoChange}
              onUpcomingBirthdayDaysChange={headerData.onUpcomingBirthdayDaysChange}
              sortMode={headerData.sortMode}
              onSortModeChange={headerData.onSortModeChange}
            />
            )}
          </div>
        )}
      </div>

      {auth.isAuthenticated && (
        <div className="flex items-center gap-2 shrink-0">
          {!isMobile && userName && (
            <span className="text-[12px] font-medium text-gray-700 truncate max-w-[160px]">
              {userName}
            </span>
          )}
          {!isMobile && roleLabel && (
            <span className="px-1.5 py-0 text-[10px] font-semibold rounded bg-gray-900 text-white leading-[18px]">
              {roleLabel}
            </span>
          )}
          {headerData && (
            <UserPopover
              currentUser={headerData.currentUser}
              activeGroup={headerData.activeGroup}
              onManageStaffClick={headerData.onManageStaffClick}
              onLogoutClick={headerData.onLogoutClick}
              onClickPermission={headerData.onClickPermission}
              onClickSetting={headerData.onClickSetting}
              onClickHiddenConversations={headerData.onClickHiddenConversations}
              onOpenStagesManager={headerData.onOpenStagesManager}
              showManageStaffButton={headerData.showManageStaffButton}
              showPermissionButton={headerData.showPermissionButton}
              showStagesManagerButton={headerData.showStagesManagerButton}
            />
          )}
          {!isMobile && (
            <button
              onClick={logoutHandler}
              title="Đăng xuất"
              aria-label="Đăng xuất"
              className="p-1 rounded hover:bg-gray-100 text-gray-600 transition-colors focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:outline-none"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      )}
    </header>
  );
};

function getRoleLabel(role) {
  if (!role) return "";
  const roleStr = Array.isArray(role) ? role.join(",") : String(role);
  if (roleStr.includes("super_admin")) return "Super Admin";
  if (roleStr.includes("admin")) return "Admin";
  if (roleStr.includes("manager")) return "Manager";
  if (roleStr.includes("staff")) return "Staff";
  return "";
}
